/**
 * Geofence Containment Utilities
 *
 * Provides algorithms for checking if geofences are contained within each other,
 * calculating adjustments needed for containment, and computing participant centroids.
 */

import type { GeoLocation } from '@/lib/types'
import { calculateDistance } from './geofence'

export interface GeofenceBoundary {
  center: GeoLocation
  radiusMeters: number
}

export interface ContainmentResult {
  isContained: boolean
  overlapPercentage: number
  distanceBetweenCenters: number
  maxAllowedRadius?: number
}

export interface AdjustmentResult {
  needsAdjustment: boolean
  suggestedCenter?: GeoLocation
  suggestedRadius?: number
  reason?: string
}

export interface CentroidResult {
  centroid: GeoLocation
  participantCount: number
  maxDistanceFromCentroid: number
  suggestedRadius: number
}

/**
 * Check if an inner geofence is completely contained within an outer geofence
 *
 * A geofence is contained if the distance from outer center to inner center
 * plus the inner radius is less than or equal to the outer radius.
 *
 * @param inner - The inner geofence (e.g., sector)
 * @param outer - The outer geofence (e.g., command room)
 * @returns ContainmentResult with details about containment status
 */
export function isGeofenceContained(
  inner: GeofenceBoundary,
  outer: GeofenceBoundary
): ContainmentResult {
  const distanceBetweenCenters = calculateDistance(inner.center, outer.center)

  // For inner to be fully contained:
  // distanceBetweenCenters + inner.radius <= outer.radius
  const isContained = distanceBetweenCenters + inner.radiusMeters <= outer.radiusMeters

  // Calculate how much of inner is within outer (approximate)
  // If fully contained, overlapPercentage is 100%
  // If not contained, calculate based on how far out it extends
  let overlapPercentage = 100
  if (!isContained) {
    const overExtension = (distanceBetweenCenters + inner.radiusMeters) - outer.radiusMeters
    // Rough approximation: what percentage of the inner radius is outside
    const percentOutside = Math.min(100, (overExtension / inner.radiusMeters) * 100)
    overlapPercentage = Math.max(0, 100 - percentOutside)
  }

  // Max allowed radius for inner at current position
  const maxAllowedRadius = Math.max(0, outer.radiusMeters - distanceBetweenCenters)

  return {
    isContained,
    overlapPercentage,
    distanceBetweenCenters,
    maxAllowedRadius,
  }
}

/**
 * Calculate the adjustment needed to fit an inner geofence within an outer geofence
 *
 * @param inner - The inner geofence that needs to fit
 * @param outer - The outer geofence boundary
 * @returns AdjustmentResult with suggested changes
 */
export function calculateContainmentAdjustment(
  inner: GeofenceBoundary,
  outer: GeofenceBoundary
): AdjustmentResult {
  const containment = isGeofenceContained(inner, outer)

  if (containment.isContained) {
    return { needsAdjustment: false }
  }

  const distanceBetweenCenters = containment.distanceBetweenCenters

  // Option 1: Reduce radius to fit at current position
  const maxRadiusAtCurrentPos = outer.radiusMeters - distanceBetweenCenters
  if (maxRadiusAtCurrentPos >= 50) { // Minimum viable radius of 50m
    return {
      needsAdjustment: true,
      suggestedRadius: maxRadiusAtCurrentPos,
      reason: `Reduce radius to ${Math.round(maxRadiusAtCurrentPos)}m to fit within boundary`,
    }
  }

  // Option 2: Move center toward outer center
  // Calculate position along the line between centers that allows inner to fit
  const requiredDistance = outer.radiusMeters - inner.radiusMeters
  if (requiredDistance > 0) {
    // Move inner center toward outer center by the necessary amount
    const moveRatio = requiredDistance / distanceBetweenCenters

    // Linear interpolation of coordinates
    const suggestedLat = outer.center.latitude +
      (inner.center.latitude - outer.center.latitude) * moveRatio
    const suggestedLng = outer.center.longitude +
      (inner.center.longitude - outer.center.longitude) * moveRatio

    return {
      needsAdjustment: true,
      suggestedCenter: {
        latitude: suggestedLat,
        longitude: suggestedLng,
      },
      reason: 'Move center closer to command boundary center to fit',
    }
  }

  // Option 3: Both move and reduce radius (when inner radius > outer radius)
  if (inner.radiusMeters > outer.radiusMeters) {
    return {
      needsAdjustment: true,
      suggestedCenter: outer.center,
      suggestedRadius: outer.radiusMeters * 0.9, // Leave some buffer
      reason: 'Sector radius exceeds command boundary - reduce radius and center on command',
    }
  }

  return {
    needsAdjustment: true,
    suggestedCenter: outer.center,
    suggestedRadius: Math.min(inner.radiusMeters, outer.radiusMeters * 0.9),
    reason: 'Adjust both center and radius to fit within boundary',
  }
}

/**
 * Calculate the centroid of participant locations
 *
 * Uses geographic center calculation suitable for local areas.
 * For global distribution, spherical averaging would be needed.
 *
 * @param locations - Array of participant locations
 * @returns CentroidResult with centroid and statistics
 */
export function calculateParticipantCentroid(locations: GeoLocation[]): CentroidResult | null {
  if (locations.length === 0) {
    return null
  }

  if (locations.length === 1) {
    const loc = locations[0]
    if (!loc) return null
    return {
      centroid: loc,
      participantCount: 1,
      maxDistanceFromCentroid: 0,
      suggestedRadius: 50, // Default minimum
    }
  }

  // Calculate arithmetic mean of coordinates (valid for local areas)
  let sumLat = 0
  let sumLng = 0
  for (const loc of locations) {
    sumLat += loc.latitude
    sumLng += loc.longitude
  }

  const centroid: GeoLocation = {
    latitude: sumLat / locations.length,
    longitude: sumLng / locations.length,
  }

  // Calculate max distance from centroid and suggested radius
  let maxDistance = 0
  for (const loc of locations) {
    const distance = calculateDistance(centroid, loc)
    if (distance > maxDistance) {
      maxDistance = distance
    }
  }

  // Suggested radius is max distance plus 20% buffer, minimum 50m
  const suggestedRadius = Math.max(50, Math.ceil(maxDistance * 1.2))

  return {
    centroid,
    participantCount: locations.length,
    maxDistanceFromCentroid: maxDistance,
    suggestedRadius,
  }
}

/**
 * Calculate the drift between current boundary center and participant centroid
 *
 * @param currentCenter - Current geofence center
 * @param participantCentroid - Calculated participant centroid
 * @returns Distance in meters that the boundary has drifted from participants
 */
export function calculateCentroidDrift(
  currentCenter: GeoLocation,
  participantCentroid: GeoLocation
): number {
  return calculateDistance(currentCenter, participantCentroid)
}

/**
 * Determine if an auto-adjustment should be suggested based on centroid drift
 *
 * @param currentBoundary - Current geofence boundary
 * @param participantLocations - Array of participant locations
 * @param driftThreshold - Percentage of radius that triggers suggestion (default 25%)
 * @returns AdjustmentResult if adjustment is suggested
 */
export function suggestBoundaryAdjustment(
  currentBoundary: GeofenceBoundary,
  participantLocations: GeoLocation[],
  driftThreshold: number = 0.25
): AdjustmentResult {
  const centroidResult = calculateParticipantCentroid(participantLocations)
  if (!centroidResult || centroidResult.participantCount < 2) {
    return { needsAdjustment: false }
  }

  const drift = calculateCentroidDrift(currentBoundary.center, centroidResult.centroid)
  const thresholdDistance = currentBoundary.radiusMeters * driftThreshold

  // Check if participants have drifted significantly
  if (drift > thresholdDistance) {
    return {
      needsAdjustment: true,
      suggestedCenter: centroidResult.centroid,
      suggestedRadius: Math.max(currentBoundary.radiusMeters, centroidResult.suggestedRadius),
      reason: `Participants have moved ${Math.round(drift)}m from center. Consider re-centering on group.`,
    }
  }

  // Check if current radius is too large (participants clustered in small area)
  if (centroidResult.suggestedRadius < currentBoundary.radiusMeters * 0.5) {
    return {
      needsAdjustment: true,
      suggestedRadius: centroidResult.suggestedRadius,
      reason: `Participants are clustered. Consider reducing radius to ${Math.round(centroidResult.suggestedRadius)}m.`,
    }
  }

  // Check if current radius is too small (participants spread beyond boundary)
  if (centroidResult.maxDistanceFromCentroid > currentBoundary.radiusMeters * 0.9) {
    return {
      needsAdjustment: true,
      suggestedRadius: centroidResult.suggestedRadius,
      reason: `Some participants may be outside boundary. Consider increasing radius to ${Math.round(centroidResult.suggestedRadius)}m.`,
    }
  }

  return { needsAdjustment: false }
}

/**
 * Validate that a proposed sector boundary is valid within a command boundary
 *
 * @param sectorBoundary - Proposed sector boundary
 * @param commandBoundary - Command room boundary
 * @returns Validation result with error message if invalid
 */
export function validateSectorBoundary(
  sectorBoundary: GeofenceBoundary,
  commandBoundary: GeofenceBoundary
): { valid: boolean; error?: string; containment: ContainmentResult } {
  const containment = isGeofenceContained(sectorBoundary, commandBoundary)

  if (!containment.isContained) {
    const adjustment = calculateContainmentAdjustment(sectorBoundary, commandBoundary)
    return {
      valid: false,
      error: `Sector boundary extends beyond command boundary. ${adjustment.reason}`,
      containment,
    }
  }

  // Warn if sector is very small compared to command
  if (sectorBoundary.radiusMeters < commandBoundary.radiusMeters * 0.1) {
    return {
      valid: true,
      error: 'Warning: Sector is very small compared to command area',
      containment,
    }
  }

  return { valid: true, containment }
}

/**
 * Check if multiple sectors have any overlap
 *
 * @param sectors - Array of sector boundaries
 * @returns Array of pairs of overlapping sector indices
 */
export function findOverlappingSectors(
  sectors: GeofenceBoundary[]
): Array<[number, number]> {
  const overlaps: Array<[number, number]> = []

  for (let i = 0; i < sectors.length; i++) {
    for (let j = i + 1; j < sectors.length; j++) {
      const sector1 = sectors[i]
      const sector2 = sectors[j]
      if (!sector1 || !sector2) continue

      const distance = calculateDistance(sector1.center, sector2.center)
      // Sectors overlap if distance between centers < sum of radii
      if (distance < sector1.radiusMeters + sector2.radiusMeters) {
        overlaps.push([i, j])
      }
    }
  }

  return overlaps
}
